import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Bell,
    Search,
    MapPin,
    CalendarDays,
    Briefcase,
    Users,
    Compass,
    MoreHorizontal,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { getPublicEvents, getPublicServices } from "@/lib/entitiesStore";
import { FeaturedEventsRow } from "@/components/EventCard";
import { PopularServicesRow } from "@/components/ServiceCard";
import { useSocialVersion } from "@/lib/hooks";
import { Sun, Moon } from "lucide-react";

const shortcuts = [
    { id: "events",    label: "Events",    icon: CalendarDays, to: "/discover/events" },
    { id: "services",  label: "Services",  icon: Briefcase,    to: "/discover/services" },
    { id: "people",    label: "People",    icon: Users,        to: "/discover/people" },
    { id: "near-me",   label: "Near Me",   icon: Compass,      to: "/near-me" },
    { id: "more",      label: "More",      icon: MoreHorizontal, to: "/discover/more" },
];

const Home = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const firstName = user?.fullName?.split(" ")[0] || "there";

    // Personalise feed: if user picked interest ids that match event/service tags, lift them up.
    const interests = Array.isArray(user?.interests) ? user.interests : [];
    const v = useSocialVersion();
    const events = useMemo(() => {
        const all = getPublicEvents();
        if (!interests.length) return all;
        const matches = all.filter((e) => Array.isArray(e?.tags) && e.tags.some((t) => interests.includes(t)));
        const rest = all.filter((e) => !matches.includes(e));
        return [...matches, ...rest];
    }, [interests, v]);
    const services = useMemo(() => {
        const all = getPublicServices();
        if (!interests.length) return all;
        const matches = all.filter((s) => Array.isArray(s?.tags) && s.tags.some((t) => interests.includes(t)));
        const rest = all.filter((s) => !matches.includes(s));
        return [...matches, ...rest];
    }, [interests, v]);

    // Derived discovery rails — trending (by reviews count), top rated, new.
    const allServices = useMemo(() => getPublicServices(), [v]);
    const trendingServices = useMemo(
        () => [...allServices].sort((a, b) => (b?.reviews || 0) - (a?.reviews || 0)).slice(0, 8),
        [allServices]
    );
    const topRatedServices = useMemo(
        () => [...allServices]
            .filter((s) => (s?.rating || 0) > 0)
            .sort((a, b) => (b?.rating || 0) - (a?.rating || 0))
            .slice(0, 8),
        [allServices]
    );
    const newServices = useMemo(
        () => [...allServices]
            .sort((a, b) => (b?.createdAt || 0) - (a?.createdAt || 0))
            .slice(0, 8),
        [allServices]
    );
    const topCategories = useMemo(() => {
        const map = new Map();
        for (const s of allServices) {
            const c = s?.category || "Other";
            map.set(c, (map.get(c) || 0) + 1);
        }
        return [...map.entries()]
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8)
            .map(([name, count]) => ({ name, count }));
    }, [allServices]);

    return (
        <div className="min-h-full animate-fade-in" data-testid="home-screen">
            {/* Header */}
            <header
                className="sticky top-0 z-20 px-5 pt-[max(14px,env(safe-area-inset-top))] pb-3"
                style={{
                    background:
                        "linear-gradient(180deg, hsl(var(--background)) 0%, hsl(var(--background) / 0.88) 70%, hsl(var(--background) / 0) 100%)",
                    backdropFilter: "saturate(140%) blur(8px)",
                    WebkitBackdropFilter: "saturate(140%) blur(8px)",
                }}
                data-testid="home-header"
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <Logo size={34} />
                        <div className="min-w-0">
                            <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground leading-none">
                                Hi, {firstName}
                            </p>
                            <button
                                data-testid="home-location-pill"
                                onClick={() => navigate(user?.location ? "/near-me" : "/profile/edit")}
                                className="mt-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[12px] font-medium hover:bg-primary/15 active:scale-[0.98] transition-[transform,background-color] duration-200"
                            >
                                <MapPin size={12} strokeWidth={2.6} />
                                {user?.location || "Set location"}
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                        <button
                            data-testid="home-theme-toggle"
                            onClick={toggleTheme}
                            className="relative h-10 w-10 flex items-center justify-center rounded-xl bg-secondary text-secondary-foreground hover:bg-muted active:scale-95 transition-[transform,background-color] duration-150"
                            aria-label="Toggle theme"
                        >
                            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                        </button>
                        <button
                            data-testid="home-notifications-btn"
                            onClick={() => navigate("/notifications")}
                            className="relative h-10 w-10 flex items-center justify-center rounded-xl bg-secondary text-secondary-foreground hover:bg-muted active:scale-95 transition-[transform,background-color] duration-150"
                            aria-label="Activity"
                        >
                            <Bell size={18} />
                        </button>
                    </div>
                </div>

                {/* Search bar */}
                <button
                    data-testid="home-search-bar"
                    onClick={() => navigate("/search")}
                    className="mt-3 w-full flex items-center gap-3 h-12 px-4 rounded-full bg-secondary hover:bg-muted border border-transparent focus:border-primary transition-[background-color,box-shadow,border-color] duration-200 shadow-soft"
                >
                    <Search size={17} className="text-muted-foreground" />
                    <span className="text-[14px] text-muted-foreground text-left flex-1">
                        Search events, services, people
                    </span>
                </button>
            </header>

            {/* Shortcuts */}
            <section
                className="px-5 pt-5"
                data-testid="home-shortcuts-section"
            >
                <div className="grid grid-cols-5 gap-2">
                    {shortcuts.map((s) => (
                        <Shortcut key={s.id} {...s} />
                    ))}
                </div>
            </section>

            {/* Featured Events */}
            <section className="pt-7" data-testid="home-featured-section">
                <SectionHeader
                    title="Featured Events"
                    onSeeAll={() => navigate("/discover/events")}
                    seeAllTestId="home-featured-see-all"
                />
                <FeaturedEventsRow events={events} />
            </section>

            {/* Popular Services */}
            <section className="pt-6" data-testid="home-services-section">
                <SectionHeader
                    title="Popular Services"
                    onSeeAll={() => navigate("/discover/services")}
                    seeAllTestId="home-services-see-all"
                />
                <PopularServicesRow services={services} />
            </section>

            {/* Trending */}
            {trendingServices.length > 0 && (
                <section className="pt-6" data-testid="home-trending-section">
                    <SectionHeader
                        title="Trending Now"
                        onSeeAll={() => navigate("/discover/services")}
                        seeAllTestId="home-trending-see-all"
                    />
                    <PopularServicesRow services={trendingServices} />
                </section>
            )}

            {/* Top Rated */}
            {topRatedServices.length > 0 && (
                <section className="pt-6" data-testid="home-top-rated-section">
                    <SectionHeader
                        title="Top Rated"
                        onSeeAll={() => navigate("/discover/services")}
                        seeAllTestId="home-top-rated-see-all"
                    />
                    <PopularServicesRow services={topRatedServices} />
                </section>
            )}

            {/* New Listings */}
            {newServices.length > 0 && (
                <section className="pt-6" data-testid="home-new-section">
                    <SectionHeader
                        title="New on Neyra"
                        onSeeAll={() => navigate("/discover/services")}
                        seeAllTestId="home-new-see-all"
                    />
                    <PopularServicesRow services={newServices} />
                </section>
            )}

            {/* Categories */}
            {topCategories.length > 0 && (
                <section className="pt-6 px-5" data-testid="home-categories-section">
                    <h2 className="font-display text-[17px] font-semibold tracking-tight mb-3">Categories</h2>
                    <div className="flex flex-wrap gap-2" data-testid="home-categories-chips">
                        {topCategories.map((c) => (
                            <button
                                key={c.name}
                                data-testid={`home-category-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => navigate(`/search?q=${encodeURIComponent(c.name)}`)}
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-secondary text-foreground border border-border text-[12px] font-medium hover:bg-muted active:scale-[0.97]"
                            >
                                {c.name}
                                <span className="text-[10.5px] text-muted-foreground">{c.count}</span>
                            </button>
                        ))}
                    </div>
                </section>
            )}

            <div className="h-8" />
        </div>
    );
};

const Shortcut = ({ label, icon: Icon, to, id }) => {
    const navigate = useNavigate();
    return (
        <button
            data-testid={`shortcut-${id}`}
            onClick={() => navigate(to)}
            className="flex flex-col items-center gap-1.5 py-2 active:scale-95 transition-transform duration-150"
        >
            <span
                className="flex items-center justify-center h-12 w-12 rounded-2xl shadow-soft border border-border"
                style={{
                    background:
                        "radial-gradient(120% 120% at 30% 20%, hsl(var(--primary) / 0.20), hsl(var(--primary) / 0.06) 60%, hsl(var(--card)) 100%)",
                }}
            >
                <Icon size={20} strokeWidth={2.1} className="text-primary" />
            </span>
            <span className="text-[11px] font-medium text-foreground/80 leading-none text-center">
                {label}
            </span>
        </button>
    );
};

const SectionHeader = ({ title, onSeeAll, seeAllTestId }) => (
    <div className="flex items-center justify-between px-5 mb-3">
        <h2 className="font-display text-[17px] font-semibold tracking-tight">{title}</h2>
        <button
            data-testid={seeAllTestId}
            onClick={onSeeAll}
            className="text-[12.5px] font-medium text-primary hover:underline"
        >
            See all
        </button>
    </div>
);

export default Home;
